import React from 'react';
import {
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import {
    AttachFile as AttachFileIcon,
    Close as CloseIcon,
    InsertDriveFile as InsertDriveFileIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const AttachmentContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    backgroundColor:
        theme.palette.mode === 'dark'
            ? theme.palette.grey[900]
            : theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.divider}`,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
}));

const AttachmentGrid = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
}));

const AttachmentWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    cursor: 'pointer',
    '&:hover .deleteButton': {
        display: 'flex'
    }
}));

const ImagePreview = styled('img')(({ theme }) => ({
    width: '96px',
    height: '96px',
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        opacity: 0.8,
        transform: 'scale(1.05)',
    }
}));

const DocumentPreview = styled(Box)(({ theme }) => ({
    minWidth: 96,
    height: 96,
    borderRadius: theme.shape.borderRadius,
    backgroundColor:
        theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        opacity: 0.8,
        transform: 'scale(1.02)',
    }
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[800],
    padding: 4,
    display: 'none',
    color: theme.palette.common.white,
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[900]
            : theme.palette.grey[800],
    },
    '& .MuiSvgIcon-root': {
        fontSize: '1rem'
    },
    zIndex: 1,
}));

const AttachmentHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
}));

const AttachmentEditor = ({
    images = [],
    textAttachments = [],
    pdfAttachments = [],
    onAddAttachment,
    onRemoveAttachment,
    disabled
}) => {
    const { t } = useTranslation();
    const fileInputRef = React.useRef(null);

    const handleFileInputChange = (event) => {
        if (event.target.files.length > 0) {
            onAddAttachment(Array.from(event.target.files));
            event.target.value = '';  // Reset input
        }
    };

    const hasAttachments = images.length > 0 || textAttachments.length > 0 || pdfAttachments.length > 0;

    return (
        <Box sx={{ mt: 1 }}>
            <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
                accept="image/*,.txt,text/markdown,application/pdf"
            />
            <AttachmentContainer>
                <AttachmentGrid>
                    <Box
                        sx={{
                            mt: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <IconButton
                            size="small"
                            aria-label={t('message.addAttachment')}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={disabled}
                        >
                            <AttachFileIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {
                        images.map((image, idx) => (
                            <AttachmentWrapper key={`img-${idx}`}>
                                <ImagePreview
                                    src={image.preview || image.base64}
                                    alt={image.originalName || 'Preview'}
                                />
                                <DeleteButton
                                    className="deleteButton"
                                    onClick={() => onRemoveAttachment('image', idx)}
                                    size="small"
                                >
                                    <CloseIcon />
                                </DeleteButton>
                            </AttachmentWrapper>
                        ))
                    }
                    {
                        textAttachments.map((doc, idx) => (
                            <AttachmentWrapper key={`txt-${idx}`}>
                                <DocumentPreview>
                                    <InsertDriveFileIcon sx={{ fontSize: 32, mb: 0.5 }} />
                                    <Typography
                                        variant="caption"
                                        align="center"
                                        sx={{ width: '100%', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {doc.originalName}
                                    </Typography>
                                </DocumentPreview>
                                <DeleteButton
                                    className="deleteButton"
                                    onClick={() => onRemoveAttachment('text', idx)}
                                    size="small"
                                >
                                    <CloseIcon />
                                </DeleteButton>
                            </AttachmentWrapper>
                        ))
                    }
                    {
                        pdfAttachments.map((pdf, idx) => (
                            <AttachmentWrapper key={`pdf-${idx}`}>
                                <DocumentPreview>
                                    <PdfIcon sx={{ fontSize: 32, mb: 0.5 }} />
                                    <Typography
                                        variant="caption"
                                        align="center"
                                        sx={{ width: '100%', px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {pdf.originalName}
                                    </Typography>
                                </DocumentPreview>
                                <DeleteButton
                                    className="deleteButton"
                                    onClick={() => onRemoveAttachment('pdf', idx)}
                                    size="small"
                                >
                                    <CloseIcon />
                                </DeleteButton>
                            </AttachmentWrapper>
                        ))
                    }
                </AttachmentGrid>
                {
                    !hasAttachments && (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                            {t('message.noAttachments')}
                        </Typography>
                    )
                }
            </AttachmentContainer>
        </Box >
    );
};

export default AttachmentEditor;